/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Schedules List
 * Feature #197: Portal Light-Mode Theme, Modals & Responsive UX
 * Constitution: v1.20.0
 * Conformed to Spec 197 design blueprint
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { useSchedulesList } from '../../services/operations/metadataService';
import type { ScheduleListItem } from '../../services/operations/metadataService';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

const columns: DataTableColumn<ScheduleListItem>[] = [
  { key: 'itemName', header: 'Item', width: '30%' },
  {
    key: 'itemType',
    header: 'Type',
    width: '20%',
    render: (r) => <Badge variant="default">{r.itemType}</Badge>,
  },
  { key: 'workspaceName', header: 'Workspace', width: '30%' },
  { key: 'scheduleCount', header: 'Schedules', width: '20%', align: 'right' },
];

export const SchedulesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSchedulesList();

  return (
    <PageLayout
      helpId="auto.schedules-list"
      title="Item Schedules"
      description="Fabric items with collected job-scheduler schedule configuration"
      icon={<Clock className="h-6 w-6" />}
      actions={
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/metadata')}>
          Metadata
        </Button>
      }
    >
      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState
          title="Failed to load schedules"
          message="Unable to retrieve scheduled items list. Please try again."
        />
      ) : !data?.length ? (
        <EmptyState
          title="No schedules collected"
          message="No Fabric items with schedule data have been collected yet. Run the daily collection job to populate this list."
        />
      ) : (
        <Card padding="none">
          <DataTable
            columns={columns}
            rows={data}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/metadata/items/${r.id}/schedules`)}
            emptyMessage="No items with schedules collected yet."
          />
        </Card>
      )}
    </PageLayout>
  );
};
